import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { SETTING_TYPES, SETTING_DEFAULTS } from '../../constants';

// Base selectors
export const selectDatabase = (state: RootState) => state.database;
export const selectDbData = createSelector(selectDatabase, (database) => database.db_data);
export const selectDbCRs = createSelector(selectDatabase, (database) => database.db_crs);

// Module selectors
export const selectModuleBrands = createSelector(
  selectDbData,
  (dbData) => dbData.module_brands
);

export const selectSolarModules = createSelector(
  selectDbData,
  (dbData) => dbData.solar_modules
);

export const selectModulesByBrand = createSelector(
  [selectSolarModules, (_, brandId: string) => brandId],
  (modules, brandId) => modules
    .filter(module => module.brands.some(brand => brand.id === brandId))
);

// Inverter selectors
export const selectInverterBrands = createSelector(
  selectDbData,
  (dbData) => dbData.inverter_brands
);

export const selectInverters = createSelector(
  selectDbData,
  (dbData) => dbData.inverters
);

export const selectInvertersByBrand = createSelector(
  [selectInverters, (_, brandId: string) => brandId],
  (inverters, brandId) => inverters
    .filter(inverter => inverter.manufacturer_id === brandId)
);

export const selectOptimizers = createSelector(
  selectDbData,
  (dbData) => dbData.optimizers
);

// Combiner selectors
export const selectCombiners = createSelector(
  selectDbData,
  (dbData) => dbData.combiners
);

export const selectCombinersByBrand = createSelector(
  [selectCombiners, (_, brandId: string) => brandId],
  (combiners, brandId) => combiners
    .filter(combiner => combiner.inverter_brand_id === brandId)
);

// RSD selectors
export const selectRsds = createSelector(
  selectDbData,
  (dbData) => dbData.rsds
);

export const selectRsdsByBrand = createSelector(
  [selectRsds, (_, brandId: string) => brandId],
  (rsds, brandId) => rsds
    .filter(rsd => rsd.inverter_brand_id === brandId)
);

// Battery selectors
export const selectBatteryBrands = createSelector(
  selectDbData,
  (dbData) => dbData.battery_brands
);

export const selectBatteries = createSelector(
  selectDbData,
  (dbData) => dbData.batteries
);

export const selectBatteriesByBrand = createSelector(
  [selectBatteries, (_, brandId: string) => brandId],
  (batteries, brandId) => batteries
    .filter(battery => battery.batteryBrandId === brandId)
);

// Structural selectors
export const selectFramingSizes = createSelector(
  selectDbData,
  (dbData) => dbData.structural.framing_sizes
);

export const selectFramingTypes = createSelector(
  selectDbData,
  (dbData) => dbData.structural.framing_types
);

export const selectRafterSpacings = createSelector(
  selectDbData,
  (dbData) => dbData.structural.rafter_spacings
);

export const selectMountSystems = createSelector(
  selectDbData,
  (dbData) => dbData.structural.mount_systems || []
);

// Structural selectors with consistent naming
export const selectStructuralFramingSizes = createSelector(
  selectDbData,
  (dbData) => dbData.structural.framing_sizes
);

export const selectStructuralFramingTypes = createSelector(
  selectDbData,
  (dbData) => dbData.structural.framing_types
);

export const selectStructuralRafterSpacings = createSelector(
  selectDbData,
  (dbData) => dbData.structural.rafter_spacings
);

// MSP selectors
export const selectMspBusses = createSelector(
  selectDbData,
  (dbData) => dbData.msp.busses
);

export const selectMspBreakers = createSelector(
  selectDbData,
  (dbData) => dbData.msp.breakers
);

export const selectMspManufacturers = createSelector(
  selectDbData,
  (dbData) => dbData.msp.manufacturers
);

export const selectMspLocations = createSelector(
  selectDbData,
  (dbData) => dbData.msp.locations
);

export const selectMspStyles = createSelector(
  selectDbData,
  (dbData) => dbData.msp.styles
);

export const selectMspTypes = createSelector(
  selectDbData,
  (dbData) => dbData.msp.types
);

export const selectMspCadLocations = createSelector(
  selectDbData,
  (dbData) => dbData.msp.cad_locations
);

// Equipment selectors
export const selectPoi = createSelector(
  selectDbData,
  (dbData) => dbData.poi
);

export const selectPoiLocations = createSelector(
  selectDbData,
  (dbData) => dbData.poi_locations
);

export const selectAtsBreakerSizes = createSelector(
  selectDbData,
  (dbData) => dbData.ats_breaker_sizes
);

export const selectDisco = createSelector(
  selectDbData,
  (dbData) => dbData.disco
);

export const selectConduitSizes = createSelector(
  selectDbData,
  (dbData) => dbData.conduit.sizes
);

export const selectConduitTypes = createSelector(
  selectDbData,
  (dbData) => dbData.conduit.types
);

export const selectAttachments = createSelector(
  selectDbData,
  (dbData) => dbData.attachments
);

export const selectRails = createSelector(
  selectDbData,
  (dbData) => dbData.rails
);

// Building Attribute selectors
export const selectRoofTypes = createSelector(
  selectDbData,
  (dbData) => dbData.roof_types
);

export const selectRoofConditions = createSelector(
  selectDbData,
  (dbData) => dbData.roof_conditions
);

export const selectReroofStatuses = createSelector(
  selectDbData,
  (dbData) => dbData.reroof_statuses
);

export const selectBuildingHeights = createSelector(
  selectDbData,
  (dbData) => dbData.building_heights
);

export const selectElectricConfigs = createSelector(
  selectDbData,
  (dbData) => dbData.electric_configs
);

// Asana tasks are not stored in Redux - they're fetched on-demand in components

// Construction Reviews selectors
export const selectConstructionReviews = selectDbCRs;
export const selectDbCRsPagination = createSelector(selectDatabase, (database) => database.db_crs_pagination);

// Loading state selectors
export const selectIsLoadingDbData = createSelector(
  selectDatabase,
  (database) => database.loading.db_data
);

export const selectIsLoadingDbCRs = createSelector(
  selectDatabase,
  (database) => database.loading.db_crs
);

export const selectIsInitialized = createSelector(
  selectDatabase,
  (database) => database.is_initialized
);

// Error selectors
export const selectDbDataError = createSelector(
  selectDatabase,
  (database) => database.errors.db_data
);


export const selectDbCRsError = createSelector(
  selectDatabase,
  (database) => database.errors.db_crs
);

// Settings selectors
export const selectSettings = createSelector(
  selectDbData,
  (dbData) => dbData.settings
);

export const selectSafetyFactor = createSelector(
  selectSettings,
  (settings) => {
    const defaultValue = SETTING_DEFAULTS[SETTING_TYPES.SAFETY_FACTOR];
    const safetyFactorSetting = settings.find(s => s.type === SETTING_TYPES.SAFETY_FACTOR);
    if (!safetyFactorSetting) return defaultValue;
    const value = safetyFactorSetting.value;
    return typeof value === 'number' ? value : Number(value) || defaultValue;
  }
);

export const selectLgcyBomOffset = createSelector(
  selectSettings,
  (settings) => {
    const defaultValue = SETTING_DEFAULTS[SETTING_TYPES.LGCY_BOM_OFFSET];
    const offsetSetting = settings.find(s => s.type === SETTING_TYPES.LGCY_BOM_OFFSET);
    if (!offsetSetting) return defaultValue;
    const value = offsetSetting.value;
    return typeof value === 'number' ? value : Number(value) || defaultValue;
  }
);